import { ApolloConsumer, ApolloProvider } from "@apollo/client";
import {
  CustomThemeProvider,
  DEFAULT_THEME_OPTIONS,
  getJwt,
  splitSDKConfig,
} from "@brandclub/common-ui";
import { StyledEngineProvider } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";
import React from "react";
import { Provider as BusProvider } from "react-bus";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import App from "./App";
import "./index.css";
import reduxApolloClient from "./redux/reduxApolloClient";
import { default as getStore, default as store } from "./redux/store";
import reportWebVitals from "./reportWebVitals";
import { queryClient } from "./queryClient";

// Persist `?brandapp-v2=<value>` into localStorage before any module that
// consults `getBrandAppV2Mode()` runs at call-time. The hooks that read
// the flag (`getCustomTheme`, `getAppConfig`, `getBrandAppConfigs`) all
// read it lazily from inside their fetch functions — not at import —
// so running this after imports but before `root.render()` still wins
// the race against any API call React would fire from its effects.
// Kept inline + synchronous rather than imported so ESLint's
// `import/first` stays happy.
(() => {
  try {
    if (typeof window === "undefined") return;
    const flag = new URLSearchParams(window.location.search).get("brandapp-v2");
    // Only persist values from a fixed allowlist. The `brandapp-v2` flag
    // routes every API call to a different backend path, so a malicious
    // deep-link could otherwise pin a fresh visitor's browser into an
    // arbitrary mode for the module's lifetime. Keep the allowlist
    // inlined (rather than importing from `common-ui`) so this stamp can
    // run before any module import side-effects.
    const ALLOWED = new Set(["off", "shim", "consume"]);
    if (flag && ALLOWED.has(flag) && !window.localStorage.getItem("brandapp-v2")) {
      window.localStorage.setItem("brandapp-v2", flag);
    }
  } catch {
    // localStorage may be unavailable in some sandboxed environments; ignore.
  }
})();

const container = document.getElementById("root");
const root = createRoot(container as any);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={reduxApolloClient}>
        <ApolloConsumer>
          {(apolloClient) => (
            <Provider store={getStore(undefined, { apolloClient })}>
              <BusProvider>
                <SplitFactoryProvider config={splitSDKConfig}>
                  <StyledEngineProvider injectFirst>
                    <CustomThemeProvider themeOptions={DEFAULT_THEME_OPTIONS}>
                      <App />
                    </CustomThemeProvider>
                  </StyledEngineProvider>
                </SplitFactoryProvider>
              </BusProvider>
            </Provider>
          )}
        </ApolloConsumer>
      </ApolloProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

if (
  window.location.href.includes(import.meta.env.VITE_APP_DEV_UI_URL) ||
  window.location.href.includes(import.meta.env.VITE_APP_LOCALHOST_UI_URL)
) {
  //@ts-ignore
  window.getJwt = async () => {
    const jwt = await getJwt();
    console.log({ jwt });
  };
  //@ts-ignore
  window.store = store;
  //@ts-ignore
  window.getState = () => store().getState();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
